/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { X } from 'lucide-react'

import { cn } from 'app/lib/utils'
import { DialogA11y, useDialogA11ySlot } from 'app/components/ui/dialog-a11y'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// React 19 passes ref as a normal prop, so Radix UI's Slot/SlotClone can set
// the overlay ref on a plain function component without the legacy
// "Function components cannot be given refs" warning.
function DialogOverlay({ className, ref, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-(--scrim)',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showClose = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { showClose?: boolean }) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-surface-raised p-6 shadow-lg duration-200',
          className,
        )}
        {...props}
      >
        <DialogA11y>{children}</DialogA11y>
        {showClose && (
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  useDialogA11ySlot('title')
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold tracking-tight', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  useDialogA11ySlot('description')
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

/**
 * The app's standard dialog title bar, the one RadixModal draws: title left, close right, a
 * divider below. For a DialogContent with `p-0 gap-0 showClose={false}` that should read like
 * every other dialog in the app.
 */
function DialogTitleBar({ title, className }: { title: React.ReactNode; className?: string }) {
  return (
    <div
      data-slot="dialog-title-bar"
      className={cn('flex h-20 shrink-0 items-center justify-between border-b border-default px-6', className)}
    >
      <DialogPrimitive.Title className="text-lg leading-6 font-semibold text-strong">{title}</DialogPrimitive.Title>
      <DialogPrimitive.Close aria-label="Close" className="text-default hover:text-strong">
        <X className="h-5 w-5" />
      </DialogPrimitive.Close>
    </div>
  )
}

/**
 * The app's standard dialog footer: a divider above, the dialog's own raised surface (so its
 * rounded bottom corners show), a destructive action on the left in `start`, Cancel and the
 * primary action on the right. Works in any dialog, RadixModal or DialogContent alike.
 */
function DialogActionBar({
  start,
  children,
  className,
}: {
  start?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      data-slot="dialog-action-bar"
      className={cn(
        'flex shrink-0 items-center gap-3 rounded-b-xl border-t border-default bg-surface-raised px-6 py-3',
        className,
      )}
    >
      {start && <div className="flex min-w-0 items-center gap-3">{start}</div>}
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  )
}

export {
  Dialog,
  DialogTitleBar,
  DialogActionBar,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
